import {CoreDI, inject, Timer} from '@gongxigames/framework/core';
import {
    GenericPopupData,
    GameConfig
} from '@gongxigames/framework/slot/types';

import {SlotModuleV2} from '@gongxigames/framework/slotV2/baseClasses/slotModuleV2/SlotModuleV2';
import {GameOnlyEventMap} from '@gongxigames/framework/slotV2/globals';
import {GenericPopupViewV2} from '@gongxigames/framework/slotV2/modules/genericPopup/GenericPopupViewV2';
import {LAYERS} from '@gongxigames/framework/slot';

export class GenericPopupModuleV2<T extends GameOnlyEventMap> extends SlotModuleV2<T> {
    public static NAME = Symbol('GenericPopupModule');
    public static layer = LAYERS.POPUPS;

    protected _view!: GenericPopupViewV2;
    protected popupType?: Symbol;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    protected _closeTimer?: Timer;
    private _showPopupResolve?: () => void;

    protected onInit(): void {
        super.onInit();
        this.setView();
    }

    protected setView(): void {
        this._view = new GenericPopupViewV2({
            onContinueButtonClicked: this.onContinueButtonClicked.bind(this),
            onClosed: this.onClosed.bind(this),
            onFadeOutStarted: this.onFadeOutStarted.bind(this)
        });
        this.container.addChild(this._view.container);
    }

    protected onSetupEvents(): void {
        super.onSetupEvents();
        this.emitter.on('General.Popup.SHOW', this.onShowPopupPromise.bind(this));
        this.emitter.on('General.UI.SPACEBAR_UP', this.onSpaceBarClicked.bind(this));
    }

    protected onFadeOutStarted(): void {
        this.emitter.emit('Module.Popup.FADE_OUT_STARTED', this.popupType!);
    }

    protected onShowPopupPromise(data: GenericPopupData): Promise<void> {
        const config = this.gameConfig.popupConfig?.find(value => value.type === data.type);

        return new Promise<void>(resolve => {
            this._showPopupResolve = resolve;

            if (config) {
                this._view.initPopup(config, data);
                this.popupType = data.type;
                this.tryToShowPopup();
            } else {
                resolve();
            }
        });
    }

    protected tryToShowPopup(): void {
        const {startTimeout} = this._view.config;

        this._view.playWaitingSound();
        this._view.playTransitionEnterSound();

        if (startTimeout) {
            this.store.updateManager.value!.addTimer(this.playStart.bind(this), startTimeout, true);
        } else {
            this.playStart();
        }
    }

    protected playStart(): void {
        const {closeTimeout} = this._view.config;

        if (closeTimeout) {
            this._closeTimer = new Timer(this.onContinueButtonClicked.bind(this), closeTimeout, true);
            this.store.updateManager.value!.add(this._closeTimer);
        }

        this._view.playStart();
    }

    protected onSpaceBarClicked(): void {
        if (this._view.continueButton && this._view.continueButton.enabled) {
            this._view.onContinueButtonClicked();
        }
    }

    protected onContinueButtonClicked(): void {
        this._view.disableContinueButton();
        this.clearCloseTimer();
        this._view.playFadeOutAnimation();
    }

    protected clearCloseTimer(): void {
        if (this._closeTimer) {
            this._closeTimer.stop();
            this._closeTimer = undefined;
        }
    }

    protected onClosed(): void {
        this._showPopupResolve?.();
    }

    public get gameConfig(): GameConfig {
        return this._gameConfig;
    }
}
